<template>
  <footer class="main-footer" :style="footerStyles">
    <!-- Footer Widgets Section -->
    <FooterWidgets />
    
    <div class="container-fluid">
      <!-- Default Footer Content (fallback) -->
      <div class="row g-4" v-if="showDefaultFooter">
        <!-- Logo Column -->
        <div class="col-lg-3 col-md-6">
          <div class="footer-column">
            <div class="footer-logo-container">
              <img 
                :src="logoSrc" 
                :alt="footerData.company_name || 'Logo'"
                class="logo-img"
                @error="handleLogoError"
              >
            </div>
          </div>
        </div>

        <!-- Contact Info Column -->
        <div class="col-lg-3 col-md-6">
          <div class="footer-column">
            <h4 class="footer-title">Επικοινωνία</h4>
            <div class="contact-info">
              <div class="contact-item" v-if="footerData.contact_address && showSettings.show_address">
                <i class="bi bi-geo-alt-fill"></i>
                <div class="contact-details">
                  <p v-for="line in footerData.contact_address.split('\n')" :key="line">{{ line }}</p>
                </div>
              </div>
              <div class="contact-item" v-if="footerData.contact_phone && showSettings.show_phone">
                <i class="bi bi-telephone-fill"></i>
                <div class="contact-details">
                  <a :href="`tel:${footerData.contact_phone}`">{{ footerData.contact_phone }}</a>
                </div>
              </div>
              <div class="contact-item" v-if="footerData.contact_email && showSettings.show_email">
                <i class="bi bi-envelope-fill"></i>
                <div class="contact-details">
                  <a :href="`mailto:${footerData.contact_email}`">{{ footerData.contact_email }}</a>
                </div>
              </div>
              <div class="contact-item" v-if="footerData.working_hours && showSettings.show_working_hours">
                <i class="bi bi-clock-fill"></i>
                <div class="contact-details">
                  <p v-for="line in footerData.working_hours.split('\n')" :key="line">{{ line }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Google Maps Column -->
        <div class="col-lg-3 col-md-6" v-if="footerData.google_maps_embed && showSettings.show_google_maps">
          <div class="footer-column">
            <h4 class="footer-title">Βρείτε μας</h4>
            <div class="map-container">
              <iframe 
                :src="footerData.google_maps_embed"
                width="100%" 
                height="250" 
                style="border:0;" 
                allowfullscreen
                loading="lazy" 
                referrerpolicy="no-referrer-when-downgrade"
                :title="`Τοποθεσία ${footerData.company_name || 'Επιχείρησης'}`"
              ></iframe>
            </div>
          </div>
        </div>

        <!-- Social Media Column -->
        <div class="col-lg-3 col-md-6" v-if="hasSocialMedia">
          <div class="footer-column">
            <h4 class="footer-title">Ακολουθήστε μας</h4>
            <div class="social-container">
              <!-- Facebook Page Widget -->
              <div v-if="footerData.facebook_page_url" class="social-widget">
                <div id="fb-root"></div>
                <div class="fb-page" 
                     :data-href="footerData.facebook_page_url"
                     data-tabs="timeline" 
                     data-width="300" 
                     data-height="350" 
                     data-small-header="false" 
                     data-adapt-container-width="true" 
                     data-hide-cover="false" 
                     data-show-facepile="true">
                  <blockquote :cite="footerData.facebook_page_url" class="fb-xfbml-parse-ignore">
                    <a :href="footerData.facebook_page_url">{{ footerData.company_name || 'Facebook Page' }}</a>
                  </blockquote>
                </div>
              </div>
              
              <!-- Social Icons -->
              <div v-if="!footerData.facebook_page_url && hasSocialIcons" class="social-icons">
                <a v-if="footerData.facebook_page_url" :href="footerData.facebook_page_url" target="_blank" class="social-icon">
                  <i class="bi bi-facebook"></i>
                </a>
                <a v-if="footerData.instagram_url && showSettings.show_instagram" :href="footerData.instagram_url" target="_blank" class="social-icon">
                  <i class="bi bi-instagram"></i>
                </a>
                <a v-if="footerData.youtube_url && showSettings.show_youtube" :href="footerData.youtube_url" target="_blank" class="social-icon">
                  <i class="bi bi-youtube"></i>
                </a>
                <a v-if="footerData.twitter_url && showSettings.show_twitter" :href="footerData.twitter_url" target="_blank" class="social-icon">
                  <i class="bi bi-twitter"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer Bottom -->
      <div class="footer-bottom">
        <div class="row align-items-center">
          <div class="col-md-6">
            <p class="copyright-text">
              {{ footerData.footer_copyright || `© ${currentYear} ${footerData.company_name || 'Επιχείρηση'}. Όλα τα δικαιώματα διατηρούνται.` }}
            </p>
          </div>
          <div class="col-md-6">
            <div class="footer-links">
              <router-link to="/privacy" class="footer-link">Πολιτική Απορρήτου</router-link>
              <router-link to="/terms" class="footer-link">Όροι Χρήσης</router-link>
              <a 
                v-if="footerData.footer_powered_by_url && footerData.footer_powered_by"
                :href="footerData.footer_powered_by_url" 
                target="_blank" 
                class="footer-link"
              >
                {{ footerData.footer_powered_by }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { settingsService } from '@/services/api'
import { IMAGE_BASE_URL } from '@/utils/config'
import FooterWidgets from './FooterWidgets.vue'
import { loadFacebookSdkWhenIdle } from '@/utils/facebookSdk'

// Type declaration for Facebook SDK
declare global {
  interface Window {
    FB?: any
  }
}

// State
const logoUrl = ref('logo.png') // Default logo
const footerData = ref({
  company_name: '',
  contact_phone: '',
  contact_email: '',
  contact_address: '',
  working_hours: '',
  google_maps_embed: '',
  facebook_page_url: '',
  instagram_url: '',
  youtube_url: '',
  twitter_url: '',
  footer_copyright: '',
  footer_powered_by: '',
  footer_powered_by_url: '',
  // Footer styling
  footer_background_color: '#00C7B5',
  footer_text_color: '#ffffff',
  footer_link_color: '#ffffff',
  footer_link_hover_color: 'rgba(255,255,255,0.8)'
})

// Show/Hide settings
const showSettings = ref({
  show_phone: true,
  show_email: true,
  show_address: true,
  show_working_hours: true,
  show_google_maps: true,
  show_instagram: true,
  show_youtube: true,
  show_twitter: true
})

// Get current year for copyright
const currentYear = computed(() => new Date().getFullYear())

// Computed logo URL
const logoSrc = computed(() => {
  const file = logoUrl.value || 'logo.png'
  return `/backend/api/img.php?src=${encodeURIComponent('/backend/uploads/logos/' + file)}&h=110&q=85`
})

// Check if has social media (respecting show/hide settings)
const hasSocialMedia = computed(() => {
  return footerData.value.facebook_page_url || 
         (footerData.value.instagram_url && showSettings.value.show_instagram) || 
         (footerData.value.youtube_url && showSettings.value.show_youtube) || 
         (footerData.value.twitter_url && showSettings.value.show_twitter)
})

// Check if has social icons (but not facebook widget, respecting show/hide settings)
const hasSocialIcons = computed(() => {
  return (footerData.value.instagram_url && showSettings.value.show_instagram) || 
         (footerData.value.youtube_url && showSettings.value.show_youtube) || 
         (footerData.value.twitter_url && showSettings.value.show_twitter)
})

// Dynamic footer styles
const footerStyles = computed(() => ({
  backgroundColor: footerData.value.footer_background_color || '#00C7B5',
  color: footerData.value.footer_text_color || '#ffffff',
  '--footer-link-color': footerData.value.footer_link_color || '#ffffff',
  '--footer-link-hover-color': footerData.value.footer_link_hover_color || 'rgba(255,255,255,0.8)'
}))

// Check if we should show the default footer layout
// Show default footer only if no footer widgets are configured
const showDefaultFooter = computed(() => {
  // Return false to show only footer widgets (new widget-based footer system)
  return false
})

// Load footer data from settings
const loadFooterData = async () => {
  try {
    const response = await settingsService.getSettings()
    
    
    if (response.success && response.settings) {
      // Map settings to footer data
      Object.keys(footerData.value).forEach(key => {
        if (response.settings[key]) {
          footerData.value[key as keyof typeof footerData.value] = response.settings[key]
        }
      })
      
      // Load show/hide settings with proper boolean conversion
      showSettings.value.show_phone = response.settings.show_phone !== undefined ? 
        (response.settings.show_phone === true || response.settings.show_phone === 'true' || response.settings.show_phone === '1') : true
      showSettings.value.show_email = response.settings.show_email !== undefined ? 
        (response.settings.show_email === true || response.settings.show_email === 'true' || response.settings.show_email === '1') : true
      showSettings.value.show_address = response.settings.show_address !== undefined ? 
        (response.settings.show_address === true || response.settings.show_address === 'true' || response.settings.show_address === '1') : true
      showSettings.value.show_working_hours = response.settings.show_working_hours !== undefined ? 
        (response.settings.show_working_hours === true || response.settings.show_working_hours === 'true' || response.settings.show_working_hours === '1') : true
      showSettings.value.show_google_maps = response.settings.show_google_maps !== undefined ? 
        (response.settings.show_google_maps === true || response.settings.show_google_maps === 'true' || response.settings.show_google_maps === '1') : true
      showSettings.value.show_instagram = response.settings.show_instagram !== undefined ? 
        (response.settings.show_instagram === true || response.settings.show_instagram === 'true' || response.settings.show_instagram === '1') : true
      showSettings.value.show_youtube = response.settings.show_youtube !== undefined ? 
        (response.settings.show_youtube === true || response.settings.show_youtube === 'true' || response.settings.show_youtube === '1') : true
      showSettings.value.show_twitter = response.settings.show_twitter !== undefined ? 
        (response.settings.show_twitter === true || response.settings.show_twitter === 'true' || response.settings.show_twitter === '1') : true
      
      // Handle logo separately
      if (response.settings.logo_url) {
        logoUrl.value = response.settings.logo_url
      }
      
    }
  } catch (error) {
  }
}

// Handle logo loading error
const handleLogoError = (event: Event) => {
  const target = event.target as HTMLImageElement
  target.style.display = 'none'
  const parent = target.parentElement
  if (parent) {
    parent.innerHTML = `<div class="logo-fallback">${footerData.value.company_name || 'LOGO'}</div>`
  }
}

// Load Facebook SDK off the critical path (idle / after load)
const loadFacebookSDK = () => {
  if (footerData.value.facebook_page_url) {
    void loadFacebookSdkWhenIdle()
  }
}

onMounted(() => {
  loadFooterData()
  // Wait for LCP + first paint before even scheduling FB
  setTimeout(() => loadFacebookSDK(), 2500)
})
</script>

<style scoped>
.main-footer {
  background: #00C7B5; /* Fallback color */
  color: white; /* Fallback color */
  padding: 4rem 0 2rem;
  margin-top: auto;
  transition: background-color 0.3s ease, color 0.3s ease;
}

.footer-column {
  height: 100%;
  min-height: 200px;
  display: flex;
  flex-direction: column;
}

.footer-title {
  color: inherit; /* Use footer text color */
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid rgba(255,255,255,0.3);
  display: inline-block;
}

/* Logo Section */
.footer-logo-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  min-height: 150px;
}

.logo-img {
  max-width: 120px;
  max-height: 120px;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 12px;
  transition: transform 0.3s ease;
}

.logo-img:hover {
  transform: scale(1.05);
}

.logo-fallback {
  color: #ffffff;
  padding: 20px 15px;
  border-radius: 12px;
  font-weight: bold;
  font-size: 1rem;
  text-align: center;
  line-height: 1.2;
  min-width: 120px;
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Contact Info */
.contact-info {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1;
}

.contact-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.contact-item i {
  color: rgba(255,255,255,0.8);
  font-size: 1.1rem;
  margin-top: 0.1rem;
  flex-shrink: 0;
}

.contact-details p {
  margin: 0;
  color: inherit;
  line-height: 1.4;
}

.contact-details a {
  color: var(--footer-link-color, #ffffff);
  text-decoration: none;
  transition: color 0.3s ease;
}

.contact-details a:hover {
  color: var(--footer-link-hover-color, rgba(255,255,255,0.8));
}

/* Map Container */
.map-container {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0,0,0,0.2);
  flex: 1;
  min-height: 150px;
}

.map-container iframe {
  border-radius: 8px;
  max-width: 100%;
  height: 100%;
  min-height: 150px;
}

/* Social Container */
.social-container {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0,0,0,0.2);
  flex: 1;
  min-height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.social-widget {
  width: 100%;
  height: 100%;
}

.fb-page {
  width: 100% !important;
  height: 100% !important;
}

.social-icons {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
}

.social-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #0C5A85, #0a4563);
  color: white;
  border-radius: 50%;
  text-decoration: none;
  font-size: 1.5rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.social-icon:hover {
  transform: translateY(-3px);
  box-shadow: 0 5px 15px rgba(59, 130, 246, 0.4);
  color: white;
}

/* Footer Bottom */
.footer-bottom {
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid rgba(255,255,255,0.2);
}

.copyright-text {
  margin: 0;
  color: var(--footer-link-color, rgba(255,255,255,0.8));
  font-size: 0.9rem;
  opacity: 0.9;
}

.footer-links {
  display: flex;
  gap: 1.5rem;
  justify-content: flex-end;
  flex-wrap: wrap;
}

.footer-link {
  color: var(--footer-link-color, rgba(255,255,255,0.8));
  text-decoration: none;
  font-size: 0.9rem;
  transition: color 0.3s ease;
}

.footer-link:hover {
  color: var(--footer-link-hover-color, #ffffff);
}

/* Responsive Design */
@media (max-width: 992px) {
  .main-footer {
    padding: 3rem 0 1.5rem;
  }
  
  .footer-title {
    font-size: 1.2rem;
  }
  
  .social-container iframe {
    height: 150px;
  }
}

@media (max-width: 768px) {
  .main-footer {
    padding: 2rem 0 1rem;
  }
  
  .footer-logo {
    flex-direction: column;
    text-align: center;
    gap: 0.5rem;
  }
  
  .contact-item {
    align-items: center;
  }
  
  .map-container iframe {
    height: 120px;
  }
  
  .social-container iframe {
    height: 120px;
  }
  
  .footer-links {
    justify-content: center;
    margin-top: 1rem;
  }
  
  .footer-bottom {
    text-align: center;
  }
  
  .footer-bottom .row {
    flex-direction: column;
    gap: 1rem;
  }
  
  .footer-bottom .col-md-6 {
    text-align: center;
  }
  
  .copyright-text {
    text-align: center;
  }
}

@media (max-width: 576px) {
  .footer-links {
    flex-direction: column;
    gap: 0.5rem;
    align-items: center;
    text-align: center;
  }
  
  .contact-item {
    flex-direction: column;
    text-align: center;
    gap: 0.3rem;
  }
  
  .contact-item i {
    margin-top: 0;
  }
  
  .copyright-text {
    font-size: 0.85rem;
    text-align: center;
  }
  
  .footer-link {
    font-size: 0.85rem;
  }
}
</style>
